import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Fuse from "fuse.js";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { ROUTES } from "../config/routes";
import { PendingApplicationResponse } from "../api/generated/models/PendingApplicationResponse";
import { CompletedApplicationResponse } from "../api/generated/models/CompletedApplicationResponse";
import { RejectedApplicationResponse } from "../api/generated/models/RejectedApplicationResponse";
import { CompanyViewModel } from "../api/generated/models/CompanyViewModel";
import { PartnerAccountViewModel } from "../api/generated/models/PartnerAccountViewModel";
import { AcceptedTermsOfServiceItemViewModel } from "../api/generated/models/AcceptedTermsOfServiceItemViewModel";
import { PartnerOnboardingService } from "../api/generated/services/PartnerOnboardingService";
import { BusinessAccountService } from "../api/generated/services/BusinessAccountService";
import { PartnerAccountsService } from "../api/generated/services/PartnerAccountsService";
import { ReactComponent as RefreshIcon } from "../assets/icons/refresh.svg";
import SubmitApplicationModal from "../components/PartnerManagement/modals/SubmitApplicationModal";
import AddPartnerModal from "../components/PartnerManagement/modals/AddPartnerModal";
import PartnerActionsPopover from "../components/PartnerManagement/PartnerActionsPopover";
import "../api/openApiConfig";

// Combined type for company with its partner accounts
type PartnerWithAccounts = CompanyViewModel & {
  partnerAccounts: PartnerAccountViewModel[];
};

type TabType = "pending" | "completed" | "rejected";
type TosFilterType = "all" | "any" | "older" | "latest";
type DashboardAccessFilterType = "any" | "no_access" | "pending_invitation" | "accepted";

const PartnerManagement: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("pending");
  const [pendingApplications, setPendingApplications] = useState<
    PendingApplicationResponse[]
  >([]);
  const [completedApplications, setCompletedApplications] = useState<
    CompletedApplicationResponse[]
  >([]);
  const [rejectedApplications, setRejectedApplications] = useState<
    RejectedApplicationResponse[]
  >([]);
  const [partners, setPartners] = useState<PartnerWithAccounts[]>([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersSearch, setPartnersSearch] = useState("");
  const [acceptedTos, setAcceptedTos] = useState<Record<string, number>>({});
  const [tosLoading, setTosLoading] = useState(false);
  const [latestTosVersion, setLatestTosVersion] = useState<number | null>(null);
  const [tosFilter, setTosFilter] = useState<TosFilterType>("all");
  const [dashboardAccessFilter, setDashboardAccessFilter] = useState<DashboardAccessFilterType>("any");
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitApplicationModalOpen, setIsSubmitApplicationModalOpen] = useState(false);
  const [isAddPartnerModalOpen, setIsAddPartnerModalOpen] = useState(false);

  // Auto-refresh state
  const isRefreshingRef = useRef(false);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchApplications = useCallback(async (isAutoRefresh = false) => {
    // Prevent simultaneous refreshes
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      if (!isAutoRefresh) setLoading(true);
      if (activeTab === "pending") {
        const response =
          await PartnerOnboardingService.getApiPartnerOnboardingPendingApplications();
        setPendingApplications(response);
      } else if (activeTab === "completed") {
        const response =
          await PartnerOnboardingService.getApiPartnerOnboardingCompletedApplications();
        setCompletedApplications(response);
      } else if (activeTab === "rejected") {
        const response =
          await PartnerOnboardingService.getApiPartnerOnboardingRejectedApplications();
        setRejectedApplications(response);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching applications:", err);
      if (!isAutoRefresh) {
        setError("Failed to load applications. Please try again.");
      }
    } finally {
      if (!isAutoRefresh) setLoading(false);
      setInitialLoad(false);
      isRefreshingRef.current = false;
    }
  }, [activeTab]);

  // Calculate refresh interval for in-progress applications
  const getRefreshInterval = useCallback(() => {
    if (activeTab !== "pending") return null;

    const inProgressApps = pendingApplications.filter(
      (app) => app.isOnboardingInProgress
    );

    if (inProgressApps.length === 0) return null;

    // Find the most recently approved application that's still in progress
    let shortestInterval: number | null = null;

    for (const app of inProgressApps) {
      if (!app.approvedAt) continue;

      const approvedAt = new Date(app.approvedAt).getTime();
      const now = Date.now();
      const secondsSinceApproval = (now - approvedAt) / 1000;

      // Stop refreshing if approved > 1 minute ago
      if (secondsSinceApproval > 60) continue;

      // 10 second interval if approved > 30 seconds ago
      if (secondsSinceApproval > 30) {
        if (shortestInterval === null || 10000 < shortestInterval) {
          shortestInterval = 10000;
        }
      } else {
        // 1 second interval for recently approved
        shortestInterval = 1000;
        break; // Can't get shorter than 1 second
      }
    }

    return shortestInterval;
  }, [activeTab, pendingApplications]);

  // Initial fetch
  useEffect(() => {
    fetchApplications();
  }, [activeTab, fetchApplications]);

  // Auto-refresh effect
  useEffect(() => {
    const scheduleNextRefresh = () => {
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      const interval = getRefreshInterval();
      if (interval === null) return;

      refreshTimeoutRef.current = setTimeout(async () => {
        await fetchApplications(true);
        // Ensure at least 1 second delay after refresh completes
        await new Promise((resolve) => setTimeout(resolve, 1000));
        scheduleNextRefresh();
      }, interval);
    };

    scheduleNextRefresh();

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [getRefreshInterval, fetchApplications]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSubmitApplicationSuccess = useCallback(async (applicationId: string) => {
    // Helper to fetch pending applications and check if the submitted one exists
    const checkApplicationExists = async (): Promise<boolean> => {
      try {
        const response = await PartnerOnboardingService.getApiPartnerOnboardingPendingApplications();
        setPendingApplications(response);
        return response.some(app => app.partnerApplicationId === applicationId);
      } catch (err) {
        console.error("Error checking for application:", err);
        return false;
      }
    };

    // Retry logic: check immediately, then after 1s, then after 2s
    const delays = [0, 1000, 2000];

    for (const delay of delays) {
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      const exists = await checkApplicationExists();
      if (exists) {
        break;
      }
    }

    setSuccessMessage("Application submitted successfully.");
  }, []);

  const handleAddPartnerSuccess = (applicationId: string) => {
    navigate(ROUTES.ADMIN_PARTNER_APPLICATION_DETAILS(applicationId));
  };

  // Fetch partners (companies + partner accounts)
  const fetchPartners = useCallback(async () => {
    try {
      setPartnersLoading(true);

      // Fetch companies and partner accounts in parallel
      const [companiesResponse, partnerAccountsResponse] = await Promise.all([
        BusinessAccountService.postApiBusinessAccountListCompaniesForAdmin({}),
        PartnerAccountsService.postApiPartnerAccountsListPartnerAccounts({}),
      ]);

      // Group partner accounts by companyId
      const accountsByCompanyId = partnerAccountsResponse.reduce<
        Record<string, PartnerAccountViewModel[]>
      >((acc, account) => {
        const companyId = account.companyId;
        if (companyId) {
          if (!acc[companyId]) {
            acc[companyId] = [];
          }
          acc[companyId].push(account);
        }
        return acc;
      }, {});

      // Combine companies with their partner accounts
      const partnersWithAccounts: PartnerWithAccounts[] = companiesResponse.map(
        (company) => ({
          ...company,
          partnerAccounts: company.id ? accountsByCompanyId[company.id] || [] : [],
        })
      );

      setPartners(partnersWithAccounts);
    } catch (err) {
      console.error("Error fetching partners:", err);
    } finally {
      setPartnersLoading(false);
    }
  }, []);

  // Fetch accepted TOS for all companies
  const fetchAcceptedTos = useCallback(async () => {
    try {
      setTosLoading(true);
      const response = await BusinessAccountService.postApiBusinessAccountGetAllAcceptedTermsOfService({});

      // Convert array to a lookup map by companyId
      const tosMap = response.reduce<Record<string, number>>((acc, item) => {
        if (item.companyId && item.acceptedTermsOfServiceVersion !== undefined) {
          acc[item.companyId] = item.acceptedTermsOfServiceVersion;
        }
        return acc;
      }, {});

      setAcceptedTos(tosMap);
    } catch (err) {
      console.error("Error fetching accepted TOS:", err);
    } finally {
      setTosLoading(false);
    }
  }, []);

  // Fetch latest TOS version
  const fetchLatestTos = useCallback(async () => {
    try {
      const response = await BusinessAccountService.postApiBusinessAccountGetLatestTermsOfService({});
      if (response.version !== undefined) {
        setLatestTosVersion(response.version);
      }
    } catch (err) {
      console.error("Error fetching latest TOS:", err);
    }
  }, []);

  // Initial fetch for partners and TOS
  useEffect(() => {
    fetchPartners();
    fetchAcceptedTos();
    fetchLatestTos();
  }, [fetchPartners, fetchAcceptedTos, fetchLatestTos]);

  // Fuse.js configuration for fuzzy search
  const partnersFuse = useMemo(() => {
    return new Fuse(partners, {
      keys: [
        "name",
        "email",
        "cvrNumber",
        "billwerkCustomerId",
        "partnerAccounts.email",
      ],
      threshold: 0.3,
      ignoreLocation: true,
    });
  }, [partners]);

  // Filtered partners based on search and TOS filter
  const filteredPartners = useMemo(() => {
    // First apply search filter
    let result = partnersSearch.trim()
      ? partnersFuse.search(partnersSearch).map((r) => r.item)
      : partners;

    // Then apply TOS filter
    if (tosFilter !== "all") {
      result = result.filter((partner) => {
        const companyId = partner.id;
        if (!companyId) return false;

        const acceptedVersion = acceptedTos[companyId];
        const hasAcceptedAny = acceptedVersion !== undefined;
        const isLatest = latestTosVersion !== null && acceptedVersion === latestTosVersion;
        const isOlder = hasAcceptedAny && latestTosVersion !== null && acceptedVersion < latestTosVersion;

        switch (tosFilter) {
          case "any":
            return hasAcceptedAny;
          case "older":
            return isOlder;
          case "latest":
            return isLatest;
          default:
            return true;
        }
      });
    }

    // Apply Dashboard Access filter
    if (dashboardAccessFilter !== "any") {
      result = result.filter((partner) => {
        const accounts = partner.partnerAccounts;
        const hasNoAccess = accounts.length === 0;
        const hasAccepted = accounts.some((acc) => acc.acceptedInvitation);
        const hasPendingInvitation = accounts.some((acc) => !acc.acceptedInvitation);

        switch (dashboardAccessFilter) {
          case "no_access":
            return hasNoAccess;
          case "pending_invitation":
            return hasPendingInvitation;
          case "accepted":
            return hasAccepted;
          default:
            return true;
        }
      });
    }

    return result;
  }, [partners, partnersSearch, partnersFuse, tosFilter, acceptedTos, latestTosVersion, dashboardAccessFilter]);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const handleViewDetails = (applicationId: string | null | undefined) => {
    if (applicationId) {
      navigate(ROUTES.ADMIN_PARTNER_APPLICATION_DETAILS(applicationId));
    }
  };

  const renderStatusBadge = (application: PendingApplicationResponse | CompletedApplicationResponse | RejectedApplicationResponse) => {
    if (activeTab === "pending") {
      const pending = application as PendingApplicationResponse;
      if (pending.isOnboardingInProgress) {
        return (
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              <svg className="w-3 h-3 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              In Progress
            </span>
            {pending.hasAnyErrors && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Error
              </span>
            )}
          </div>
        );
      }
      if (pending.approvedAt) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Approved
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          Pending
        </span>
      );
    } else if (activeTab === "completed") {
      const completed = application as CompletedApplicationResponse;
      if (completed.completedWithErrors) {
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Completed with Errors
          </span>
        );
      }
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Completed
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Rejected
        </span>
      );
    }
  };

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-10">
        <h2 className="font-medium">Partner Applications</h2>
        <Button
          onClick={() => fetchApplications()}
          variant="outline"
          size="sm"
          className="gap-1"
          disabled={loading}
        >
          <RefreshIcon className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
        <Button
          onClick={() => setActiveTab("pending")}
          variant={activeTab === "pending" ? "primary" : "outline"}
          size="sm"
        >
          Pending
        </Button>
        <Button
          onClick={() => setActiveTab("completed")}
          variant={activeTab === "completed" ? "primary" : "outline"}
          size="sm"
        >
          Completed
        </Button>
        <Button
          onClick={() => setActiveTab("rejected")}
          variant={activeTab === "rejected" ? "primary" : "outline"}
          size="sm"
        >
          Rejected
        </Button>
        </div>
        <Button
          onClick={() => setIsSubmitApplicationModalOpen(true)}
          size="sm"
        >
          Submit Application
        </Button>
      </div>

      {/* Success Notification */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Notification */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Loading indicator for subsequent fetches */}
      {loading && !initialLoad && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {/* Pending Applications Table */}
      {activeTab === "pending" && !loading && (
        pendingApplications.length === 0 ? (
          <div className="text-center text-gray-500 my-8">
            No pending applications found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Submitted At</th>
                  <th>Company Name</th>
                  <th>CVR</th>
                  <th>Contact Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingApplications.map((application) => (
                  <tr key={application.partnerApplicationId}>
                    <td>{formatDate(application.submittedAt)}</td>
                    <td className="font-medium">
                      {application.companyName || "-"}
                    </td>
                    <td>{application.cvr || "-"}</td>
                    <td>{application.contactName || "-"}</td>
                    <td>{application.email || "-"}</td>
                    <td>{application.phoneNumber || "-"}</td>
                    <td>{application.source || "-"}</td>
                    <td>{renderStatusBadge(application)}</td>
                    <td className="text-right">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() =>
                          handleViewDetails(application.partnerApplicationId)
                        }
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Completed Applications Table */}
      {activeTab === "completed" && !loading && (
        completedApplications.length === 0 ? (
          <div className="text-center text-gray-500 my-8">
            No completed applications found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Submitted At</th>
                  <th>Completed At</th>
                  <th>Company Name</th>
                  <th>CVR</th>
                  <th>Contact Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {completedApplications.map((application) => (
                  <tr key={application.partnerApplicationId}>
                    <td>{formatDate(application.submittedAt)}</td>
                    <td>{formatDate(application.completedAt)}</td>
                    <td className="font-medium">
                      {application.companyName || "-"}
                    </td>
                    <td>{application.cvr || "-"}</td>
                    <td>{application.contactName || "-"}</td>
                    <td>{application.email || "-"}</td>
                    <td>{application.phoneNumber || "-"}</td>
                    <td>{application.source || "-"}</td>
                    <td>{renderStatusBadge(application)}</td>
                    <td className="text-right">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() =>
                          handleViewDetails(application.partnerApplicationId)
                        }
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Rejected Applications Table */}
      {activeTab === "rejected" && !loading && (
        rejectedApplications.length === 0 ? (
          <div className="text-center text-gray-500 my-8">
            No rejected applications found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Submitted At</th>
                  <th>Rejected At</th>
                  <th>Company Name</th>
                  <th>CVR</th>
                  <th>Contact Name</th>
                  <th>Email</th>
                  <th>Phone Number</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rejectedApplications.map((application) => (
                  <tr key={application.partnerApplicationId}>
                    <td>{formatDate(application.submittedAt)}</td>
                    <td>{formatDate(application.rejectedAt)}</td>
                    <td className="font-medium">
                      {application.companyName || "-"}
                    </td>
                    <td>{application.cvr || "-"}</td>
                    <td>{application.contactName || "-"}</td>
                    <td>{application.email || "-"}</td>
                    <td>{application.phoneNumber || "-"}</td>
                    <td>{application.source || "-"}</td>
                    <td>{renderStatusBadge(application)}</td>
                    <td className="text-right">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() =>
                          handleViewDetails(application.partnerApplicationId)
                        }
                      >
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Partners Section */}
      <div className="flex items-center gap-3 mb-6 mt-12">
        <h2 className="font-medium">Partners</h2>
        <Button
          onClick={() => {
            fetchPartners();
            fetchAcceptedTos();
            fetchLatestTos();
          }}
          variant="outline"
          size="sm"
          className="gap-1"
          disabled={partnersLoading}
        >
          <RefreshIcon className={`w-3 h-3 ${partnersLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Search Input and Filters */}
      <div className="flex justify-between items-center gap-4 mb-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-xl">
            <input
              type="text"
              value={partnersSearch}
              onChange={(e) => setPartnersSearch(e.target.value)}
              placeholder="Search by name, email, CVR, billing ID, or dashboard access email..."
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {partnersSearch && (
              <button
                onClick={() => setPartnersSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                type="button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="tos-filter" className="text-sm text-gray-600 whitespace-nowrap">
              TOS
            </label>
            <select
              id="tos-filter"
              value={tosFilter}
              onChange={(e) => setTosFilter(e.target.value as TosFilterType)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="all">All</option>
              <option value="any">At least v1</option>
              <option value="older">Older than latest{latestTosVersion !== null ? ` (v${latestTosVersion})` : ""}</option>
              <option value="latest">Latest{latestTosVersion !== null ? ` (v${latestTosVersion})` : ""}</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="dashboard-access-filter" className="text-sm text-gray-600 whitespace-nowrap">
              Dashboard Access
            </label>
            <select
              id="dashboard-access-filter"
              value={dashboardAccessFilter}
              onChange={(e) => setDashboardAccessFilter(e.target.value as DashboardAccessFilterType)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="any">Any</option>
              <option value="no_access">No Access</option>
              <option value="pending_invitation">Pending Invitation</option>
              <option value="accepted">Accepted</option>
            </select>
          </div>
        </div>
        <Button
          onClick={() => setIsAddPartnerModalOpen(true)}
          size="sm"
        >
          Add Partner
        </Button>
      </div>

      {partnersLoading ? (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      ) : partners.length === 0 ? (
        <div className="text-center text-gray-500 my-8">
          No partners found
        </div>
      ) : filteredPartners.length === 0 ? (
        <div className="text-center text-gray-500 my-8">
          No partners match your search
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-default">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Email</th>
                <th>CVR Number</th>
                <th>Billing ID</th>
                <th>Dashboard Access</th>
                <th>TOS Version</th>
                <th>Pending Changes</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPartners.map((partner) => (
                <tr key={partner.id}>
                  <td className="font-medium">
                    {partner.name || "-"}
                  </td>
                  <td>{partner.email || "-"}</td>
                  <td>{partner.cvrNumber || "-"}</td>
                  <td>{partner.billwerkCustomerId || "-"}</td>
                  <td>
                    {partner.partnerAccounts.length === 0 ? (
                      <span className="text-gray-400">-</span>
                    ) : (
                      <div className="flex flex-col gap-1">
                        {partner.partnerAccounts.map((account) => (
                          <div
                            key={account.id}
                            className="flex items-center gap-1.5 text-sm"
                          >
                            {account.acceptedInvitation ? (
                              <svg
                                className="w-3.5 h-3.5 text-green-600 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            )}
                            <span className="truncate">{account.email}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    {tosLoading ? (
                      <div className="flex items-center">
                        <svg className="w-4 h-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      </div>
                    ) : partner.id && acceptedTos[partner.id] !== undefined ? (
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          latestTosVersion !== null && acceptedTos[partner.id] < latestTosVersion
                            ? "bg-red-100 text-red-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        v{acceptedTos[partner.id]}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td>
                    {partner.hasPendingChanges ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        No
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => partner.id && navigate(ROUTES.ADMIN_PARTNER_DETAILS(partner.id))}
                      >
                        Details
                      </Button>
                      {partner.id && <PartnerActionsPopover companyId={partner.id} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <SubmitApplicationModal
        isOpen={isSubmitApplicationModalOpen}
        onClose={() => setIsSubmitApplicationModalOpen(false)}
        onSuccess={handleSubmitApplicationSuccess}
      />

      <AddPartnerModal
        isOpen={isAddPartnerModalOpen}
        onClose={() => setIsAddPartnerModalOpen(false)}
        onSuccess={handleAddPartnerSuccess}
      />
    </div>
  );
};

export default PartnerManagement;
